import React, { forwardRef, InputHTMLAttributes } from 'react';
import classNames from 'classnames';
import {
  BOX_CLASS,
  DISABLED_CLASS,
  DRAG_CLASS,
  DROPZONE_CLASS,
  INPUT_CLASS,
  INVALID_FILE_CLASS,
  TARGET_CLASS,
} from './FileInputClasses';
import AcceptedFilesMessage from './AcceptedFilesMessage';
import FileInputInstructions from './FileInputInstructions';
import FilePreviews from './FilePreviews';
import PreviewHeading from './PreviewHeading';
import useDragAndDropFiles from './useDragAndDropFiles';

export interface FileInputProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  altInstructionText?: string;
  dragText?: string;
  language?: 'en' | 'es';
}

/**
 * The FileInput based on [The USWDS File input component](https://designsystem.digital.gov/components/form-controls/#file-input)
 */
const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      altInstructionText,
      dragText,
      language,
      id,
      multiple,
      accept,
      onChange,
      disabled,
      className,
      ...rest
    }: FileInputProps,
    ref,
  ): JSX.Element => {
    const {
      files,
      dragging,
      dragError,
      changeHandler,
      dragOverHandler,
      dragLeaveHandler,
      dropHandler,
    } = useDragAndDropFiles(accept, onChange);
    return (
      <div
        className={classNames(DROPZONE_CLASS, className, {
          [DISABLED_CLASS]: disabled,
        })}
        aria-disabled={disabled}
      >
        <div
          className={classNames(TARGET_CLASS, {
            [DRAG_CLASS]: dragging,
            [INVALID_FILE_CLASS]: dragError,
          })}
        >
          <PreviewHeading files={files} language={language} />
          <FileInputInstructions
            files={files}
            multiple={multiple}
            altInstructionText={altInstructionText}
            dragText={dragText}
            language={language}
          />
          <div className={BOX_CLASS} />
          <AcceptedFilesMessage dragError={dragError} language={language} />
          <FilePreviews files={files} />
          <input
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
            ref={ref}
            id={id}
            className={INPUT_CLASS}
            disabled={disabled}
            multiple={multiple}
            accept={accept}
            type="file"
            onChange={changeHandler}
            onDragOver={dragOverHandler}
            onDragLeave={dragLeaveHandler}
            onDrop={dropHandler}
          />
        </div>
      </div>
    );
  },
);

export default FileInput;
